import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Modal, message } from 'antd';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Drawer } from '../../../../components/index';
import {
  stopoverApi,
  useGetStopoverQuery,
} from '../../../../services/stopoverApi';
import { useBulkDeleteStoppagesMutation } from '../../../../services/stoppageApi';
import {
  ShiftType,
  Stopover,
  StoppageType,
  WorkPeriodInpactedType,
} from '../../../../types';
import { createDate } from '../../../../utils/utils';
import { WorkShiftStoppagesEditDrawer } from '../workShiftStoppagesEditDrawer/WorkShiftStoppagesEditDrawer';
import { StoppagesDrawerHeader } from './stoppagesDrawerHeader';
import { StoppagesImportModal } from './StoppagesImportModal';
import { StoppagesTable } from './stoppagesTable';

type StoppagesDrawerProps = {
  visible: boolean;
  onBack: () => void;
  selectedStopover: Stopover;
  setSelectedStopover: Dispatch<SetStateAction<Stopover>>;
};

export function StoppagesDrawer(props: StoppagesDrawerProps) {
  const { visible, onBack, selectedStopover, setSelectedStopover } = props;
  const dispatch = useDispatch();

  const [isVisibleAddStoppagesDrawer, setIsVisibleAddStoppagesDrawer] =
    useState(false);
  const [isVisibleImportModal, setIsVisibleImportModal] = useState(false);

  const [selectedStoppage, setSelectedStoppage] = useState<StoppageType>(
    {} as StoppageType
  );

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkDeleteStoppages] = useBulkDeleteStoppagesMutation();

  const { isError } = useGetStopoverQuery(Number(selectedStopover?.key) || 0, {
    skip: !visible || !selectedStopover?.key,
    pollingInterval: 0,
    refetchOnMountOrArgChange: false,
  });

  function invalidateStopoverData() {
    dispatch(
      stopoverApi.util.invalidateTags([
        { type: 'Stopover', id: selectedStopover?.key },
      ])
    );
  }

  function onEditStoppage(indexToEdit: number) {
    if (
      selectedStopover?.operation?.stoppages &&
      selectedStopover.operation.stoppages.length > 0 &&
      indexToEdit >= 0
    ) {
      const stoppage = selectedStopover.operation.stoppages.find(
        ({ id }) => id === indexToEdit
      );
      setSelectedStoppage(stoppage || ({} as StoppageType));
      setIsVisibleAddStoppagesDrawer(true);
    }
  }

  function onAddStoppage() {
    setSelectedStoppage({} as StoppageType);
    setIsVisibleAddStoppagesDrawer(true);
  }

  function onImportStoppages() {
    setIsVisibleImportModal(true);
  }

  function onCloseForm() {
    setSelectedStoppage({} as StoppageType);
    setIsVisibleAddStoppagesDrawer(false);
  }

  function onSelectRows(selectedRowKeys: React.Key[]) {
    setSelectedRowKeys(selectedRowKeys);
  }

  type ErrorDetail = {
    item: string | number;
    error_type: string;
    message: string;
    overlap_with_item?: string | number;
  };

  type ImportResponse = {
    imported_count: number;
    stoppages?: StoppageType[];
    error_count?: number;
    error_details?: ErrorDetail[];
    [key: string]: unknown;
  };

  async function handleImportSuccess(data?: ImportResponse) {
    if (data?.stoppages && data.stoppages.length > 0) {
      const oldStoppages = selectedStopover?.operation?.stoppages || [];
      setSelectedStopover({
        ...selectedStopover,
        operation: {
          ...selectedStopover.operation,
          stoppages: [...oldStoppages, ...(data?.stoppages || [])],
        },
      });
      message.success(
        `${data?.stoppages?.length || 0} paralisações importadas com sucesso!`
      );
    } else if ((data?.imported_count ?? 0) > 0) {
      try {
        invalidateStopoverData();
        message.success(
          `${data?.imported_count || 0} paralisações importadas com sucesso!`
        );
      } catch (error) {
        setSelectedStopover({ ...selectedStopover });
      }
    }
  }

  function onDeleteSelected() {
    if (selectedRowKeys.length === 0) return;

    const confirmMessage =
      selectedRowKeys.length > 5
        ? `Você está prestes a excluir ${selectedRowKeys.length} paralisações. Esta ação não poderá ser desfeita.`
        : 'Esta ação não poderá ser desfeita.';

    Modal.confirm({
      title: `Excluir ${selectedRowKeys.length} paralisação(es)?`,
      icon: <ExclamationCircleOutlined />,
      content: confirmMessage,
      okText: 'Excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await bulkDeleteStoppages({
            ids: selectedRowKeys.map((id) => Number(id)),
            operation_id: selectedStopover?.operation?.id || 0,
          });

          const deletedIds = new Set(selectedRowKeys.map((id) => Number(id)));
          const updatedStoppages =
            selectedStopover?.operation?.stoppages?.filter(
              (stoppage) => !deletedIds.has(stoppage.id)
            ) || [];

          setSelectedStopover({
            ...selectedStopover,
            operation: {
              ...selectedStopover.operation,
              stoppages: updatedStoppages,
            },
          });

          setSelectedRowKeys([]);
          message.success(
            `${selectedRowKeys.length} paralisação(es) excluída(s) com sucesso!`
          );
        } catch (error) {
          message.error('Erro ao excluir paralisações. Tente novamente.');
        }
      },
    });
  }

  const stoppagesWithPeriods = useMemo(() => {
    if (!selectedStopover?.operation?.stoppages) {
      return [];
    }
    if (!selectedStopover?.operation?.work_periods) {
      return selectedStopover.operation.stoppages.map((stoppage) => ({
        ...stoppage,
        workPeriodsInpacted: [],
        workPeriodsIndex: '',
      }));
    }

    return selectedStopover.operation.stoppages.map((stoppage) => {
      const workPeriodsInpacted: WorkPeriodInpactedType[] = [];
      for (let i = 0; i < selectedStopover.operation.work_periods.length; i++) {
        const period = selectedStopover.operation.work_periods[i];
        const workShiftsInpacted: ShiftType[] = [];
        for (let j = 0; j < period.work_shifts.length; j++) {
          const shift = period.work_shifts[j];
          const shift_start = createDate(shift.shift_start);
          const shift_end = createDate(shift.shift_end);

          if (shift_start !== null && shift_end !== null) {
            const stoppage_start = createDate(stoppage.start);
            const stoppage_end = createDate(stoppage.end);

            if (stoppage_start !== null && stoppage_end !== null) {
              if (
                (shift_start <= stoppage_start &&
                  shift_end >= stoppage_start) ||
                (shift_start <= stoppage_end && shift_end >= stoppage_end) ||
                (shift_start >= stoppage_start && shift_end <= stoppage_end)
              ) {
                workShiftsInpacted.push(period.work_shifts[j]);
              }
            }
          }
        }
        if (workShiftsInpacted.length > 0) {
          workPeriodsInpacted.push({ index: i + 1, workShiftsInpacted });
        }
      }
      return {
        ...stoppage,
        workPeriodsInpacted,
        workPeriodsIndex: workPeriodsInpacted
          .map((wp) => String(wp.index).padStart(2, '0'))
          .join(', '),
      };
    });
  }, [selectedStopover]);

  return (
    <>
      <Drawer
        visible={visible}
        width={900}
        onClose={() => onBack()}
        closable={false}
        maskClosable={false}
      >
        <StoppagesDrawerHeader
          onBack={onBack}
          onAdd={onAddStoppage}
          onImport={onImportStoppages}
          onDelete={onDeleteSelected}
          deleteCount={selectedRowKeys.length}
          deleteDisabled={selectedRowKeys.length === 0}
        />
        <div style={{ padding: '24px 16px' }}>
          {isError ? (
            <div style={{ textAlign: 'center', margin: '20px 0' }}>
              <Alert
                message="Erro ao carregar paralisações"
                description={
                  <div>
                    <p>Ocorreu um erro ao carregar as paralisações.</p>
                    <Button
                      type="primary"
                      onClick={() => invalidateStopoverData()}
                    >
                      Tentar novamente
                    </Button>
                  </div>
                }
                type="error"
                showIcon
              />
            </div>
          ) : (
            <StoppagesTable
              stoppages={stoppagesWithPeriods}
              onEdit={onEditStoppage}
              onSelectRows={onSelectRows}
              selectedRowKeys={selectedRowKeys}
            />
          )}
        </div>
      </Drawer>
      <WorkShiftStoppagesEditDrawer
        visible={isVisibleAddStoppagesDrawer}
        setIsVisibleForm={setIsVisibleAddStoppagesDrawer}
        onBack={onCloseForm}
        selectedStopover={selectedStopover}
        setSelectedStopover={setSelectedStopover}
        selectedStoppage={selectedStoppage}
        setSelectedStoppage={setSelectedStoppage}
      />
      <StoppagesImportModal
        isOpen={isVisibleImportModal}
        onCancel={() => setIsVisibleImportModal(false)}
        selectedStopover={selectedStopover}
        onSuccess={handleImportSuccess}
      />
    </>
  );
}
